import { Component, inject } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Pupil } from '../../shared/pupil.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { TableColumn } from 'src/modules/sm-base/models/table-column.model';
import { TableCellType } from 'src/modules/sm-base/models/table-cell-type.enum';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { Course } from '../../shared/course.entity';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { UpdatePupilDto } from '../../shared/update-pupils.dto';
import { SchoolType } from '../../shared/school-type.enum';

@Component({
    selector: 'app-global-pupil-list',
    templateUrl: './global-pupil-list.component.html',
    styleUrl: './global-pupil-list.component.scss'
})
export class GlobalPupilListComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { grade: "number?" };
    grade: number;

    pupils: Pupil[];
    courses: Course[];
    table: TableData<Pupil>;

    gradeLevels: number[];
    selectedGradeLevel = 7;
    coursesPerDomainAndPupil: OrdinaryObject<OrdinaryObject<number>> = {};

    gradeLevelCourses: Course[] = [];
    gradeLevelDomains: string[] = [];
        
    override async initParams(): Promise<boolean> {
        let settings = await this.service.restGetClassRegisterSettings(EnaioCertificatesTools.getCurrentYear(), false);
        if (!this.service.isPupilListEditor(settings)) {
            this.app.loadDataError("Sie besitzen nicht die notwendigen Berechtigungen, um diese Ansicht zu öffnen", false, this);
            return false;
        }
        
        this.pupils = (await this.service.restGetPupilsForYear(EnaioCertificatesTools.getCurrentSchoolYear())).filter(p => p.getSchoolType() != SchoolType.special);
        this.courses = (await this.app.loadDataHandler(async () => RestEndpoint.main().query({year: EnaioCertificatesTools.getCurrentYear(), isHalfYear: false, participants: true}).run("api/cer/classregister/courses").list(Course), this))
            .filter(c => c.isLeveledCourse);
        
        for (let course of this.courses) {
            this.coursesPerDomainAndPupil[course.levelDomain] ??= {};
            for (let pupil of course.participants) {
                this.coursesPerDomainAndPupil[course.levelDomain][pupil.pupilId] = course.id;            
            }
        }

        this.gradeLevels = EnaioCertificatesTools.getAllGradeLevels().filter(gradeLevel => gradeLevel >= 7);

        this.gradeLevelChanged();

        this.app.updateNavigation("Schülerliste", [{ label: "Schülerliste", routerLink: ["/enaio-certificates", "global-pupil-list"] }]);
        
        return true;
    }

    gradeLevelChanged(): void {
        this.gradeLevelCourses = this.courses.filter(c => c.forGrades.includes(this.selectedGradeLevel));
        this.gradeLevelDomains = Utils.arraySortByMultiple(Utils.arrayGetUnique(this.gradeLevelCourses.map(c => c.levelDomain)), [c => EnaioCertificatesTools.isForeignLanguageDomain(c), c => c]);
        let coursesPerDomain = Utils.arrayToMultiMapKeys(this.gradeLevelCourses, c => c.levelDomain);
        let pupils = this.pupils.filter(p => p.grade == this.selectedGradeLevel);
        
        this.table = new TableData([
            new TableColumn("class", "Klasse", TableCellType.text, { autoWidth: 3 }),
            new TableColumn("lastName", "Nachname", TableCellType.text, { autoWidth: 15 }),
            new TableColumn("firstName", "Vorname", TableCellType.text, { autoWidth: 15 }),
            new TableColumn("notePupilsList", "Bemerkung", TableCellType.text, { autoWidth: 30, editable: true }),
            new TableColumn("schoolCareer", "Laufbahn", TableCellType.text, { autoWidth: 20 }),
            new TableColumn("specialRequirement", "Sopäd. FSP", TableCellType.text, { autoWidth: 20 }),
            new TableColumn("secondForeignLanguage", "2. Fremdsprache", TableCellType.text, { autoWidth: 20 }),
     /*       new TableColumn("level", "Anspruchsebene", TableCellType.text, { autoWidth: 10, editable: true, editor: new FrontendFieldDefinition("field", "Feld", FrontendFieldType.comboBox, { 
                dropdownEditable: false,
                listItems: [new FrontendFieldListItem("", ""), ...EnaioCertificatesTools.getLevelTypesSingle().map(type => new FrontendFieldListItem(type, type))],
            })}),
       */     ...this.gradeLevelDomains.map(domain => new TableColumn("level_" + domain, domain, TableCellType.int, { autoWidth: 10, editable: true,
                valueFormatter: value => value == -1 ? "" : Utils.arrayFindAndConvert(coursesPerDomain[domain], c => c.id == value, c => c.getLevelTitleShort(), "???"), 
                //editableCallback: value => !EnaioCertificatesTools.isForeignLanguageDomain(domain) || domain == p.secondForeignLanguage,
                //getStyleClassCallback: (value, row) => value != -1 && this.table.rows[row].getValue("level") != "" && this.courses.find(c => c.id == value)?.levelTypeMatches(this.table.rows[row].getValue("level")) ? "xbg-red-200" : null,
                editor: new FrontendFieldDefinition("field", "Feld", FrontendFieldType.comboBox, { 
                    dropdownEditable: false,
                    listItems: [new FrontendFieldListItem(-1, ""), ...coursesPerDomain[domain].map(c => new FrontendFieldListItem(c.id, c.getLevelTitleShort()))],
                })
            }))
        ], pupils.map(p => new TableRow(Utils.toString(p.id), p, {
            class: p.getClassDisplayName(),
            lastName: p.lastName,
            firstName: p.firstName,
            notePupilsList: p.notePupilsList,
            schoolCareer: p.schoolCareer,
            specialRequirement: p.getSpecialRequirementString(),
            secondForeignLanguage: p.secondForeignLanguage,
        //    level: p.level,
            ...Utils.arrayConvertToOrdinaryObject(this.gradeLevelDomains, domain => "level_" + domain, domain => new TableCell(this.coursesPerDomainAndPupil[domain][p.id] ?? -1, { 
                styleClass: EnaioCertificatesTools.isForeignLanguageDomain(domain) && domain != p.secondForeignLanguage ? "xbg-gray-400" : null}))
        }), [new TableSortColumn("lastName")]))
    }

    
    getChanges(): {changes: string[], dto: UpdatePupilDto[]} {
        let changes: string[] = [];
        let dto: UpdatePupilDto[] = [];
        for (let row of this.table.rows) {
            let item = new UpdatePupilDto(row.raw.id);
            if (row.raw.notePupilsList != row.values.notePupilsList) {
                item.notePupilsList = row.values.notePupilsList;
                changes.push(row.raw.getFullName() + ": Bemerkung geändert"); 
            }
        /*    if (row.raw.level != row.values.level) {
                item.level = row.values.level;
                changes.push(row.raw.getFullName() + ": Anspruchsebene geändert"); 
            }
          */  for (let domain of this.gradeLevelDomains) {
                let oldValue = this.coursesPerDomainAndPupil[domain][row.raw.id] ?? -1;
                let newValue = row.getValue("level_" + domain);
                if (oldValue != newValue) {
                    if (oldValue != -1) {
                        item.removeFromCourses.push(oldValue);
                    }
                    if (newValue != -1) {
                        item.addToCourses.push(newValue);
                    }
                    changes.push(row.raw.getFullName() + ": Kurs für " + domain + " geändert: " + 
                        (this.courses.find(c => c.id == oldValue)?.getLevelTitleShort() ?? "<Nicht zugeordnet>") + " -> " + (this.courses.find(c => c.id == newValue)?.getLevelTitleShort() ?? "<Nicht zugeordnet>"));
                }
            }
            if (item.notePupilsList != null || item.level != null || item.removeFromCourses.length > 0 || item.addToCourses.length > 0) {
                dto.push(item);
            }
        }
        return { changes, dto };
    }

    isChanged(): boolean {
        return this.getChanges().dto.length > 0;
    }

    async save(): Promise<void> {
        let {changes, dto} = this.getChanges();
        if (dto.length > 0 && await this.app.messageDialog.yesNo("Die folgenden Informationen werden aktualisiert. Fortfahren?<br><br>" + Utils.arrayItemsToString(changes, "<br>"), "Bestätigung")) {
            await this.app.saveDataHandler(async () => RestEndpoint.main().query({year: EnaioCertificatesTools.getCurrentYear()}).post().body(dto).run("api/cer/pupil/updatePupils").getText(), this);
        }
        this.initParams();
    }
}
